import React, { createContext } from 'react';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { $api, unwrap, type InferResponseType } from '~/lib/api-client';

interface ISenseConfigurationProviderProps {
  children: React.ReactNode;
}

export type ISenseConfigurationResponse =
  InferResponseType<'/liftstatus/sense/configuration'>;

const SenseConfigurationContext = createContext<{
  results: 'error' | 'pending' | ISenseConfigurationResponse;
} | null>(null);

const SenseConfigurationProvider = ({
  children,
}: ISenseConfigurationProviderProps) => {
  const { status, data, error } = useQuery({
    queryKey: ['sense-configuration'],
    queryFn: () =>
      unwrap(
        $api['/liftstatus/sense/configuration'].GET({
          params: { query: { type: 'label', limit: 9999 } },
        })
      ),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const SenseConfigurations = React.useCallback(() => {
    if (status === 'pending') {
      return status;
    }

    if (status === 'error') {
      console.error('[SENSE:CONFIGURATION]:', error);
      toast.error('Error fetching Sense configurations');
      return status;
    }

    const dataToReturn = data;
    return dataToReturn;
  }, [data, error, status]);

  return (
    <SenseConfigurationContext.Provider
      value={{
        results: SenseConfigurations(),
      }}
    >
      {children}
    </SenseConfigurationContext.Provider>
  );
};

const useSenseConfiguration = () => {
  const context = React.useContext(SenseConfigurationContext);

  if (!context) {
    throw new Error(
      'useSenseConfiguration must be used within a SenseConfigurationProvider'
    );
  }

  return context;
};

export default SenseConfigurationProvider;
export { useSenseConfiguration };
